import { useCallback, useEffect, useRef, useState } from 'react';
import { pickVoice, type VoiceGender } from './voices';
import type { RefObject } from 'react';

const HOVER_DELAY_MS = 120;

function extractText(element: Element): string {
  return (element.textContent ?? '').replace(/\s+/g, ' ').trim();
}

function hasDirectText(element: Element): boolean {
  for (const node of element.childNodes) {
    if (node.nodeType === Node.TEXT_NODE && (node.textContent ?? '').trim().length > 0) return true;
  }
  return false;
}

function isReadableElement(element: Element): boolean {
  const tag = element.tagName.toLowerCase();
  const blocked = new Set([
    'script',
    'style',
    'noscript',
    'button',
    'input',
    'textarea',
    'select',
    'svg'
  ]);
  if (blocked.has(tag)) return false;
  return hasDirectText(element);
}

function nukeAudio() {
  try {
    window.speechSynthesis.pause();
  } catch {}
  try {
    window.speechSynthesis.cancel();
  } catch {}
}

export function useReadOut(
  lang: string,
  gender: VoiceGender,
  rate: number,
  pitch: number,
  suppressRef: RefObject<HTMLElement | null>,
  paused: boolean
) {
  const [active, setActive] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastElementRef = useRef<Element | null>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  // When inactive or paused: keep hammering pause+cancel every 100ms.
  // React cleans up this interval automatically when active+!paused becomes true.
  useEffect(() => {
    if (active && !paused) return;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (utteranceRef.current) {
      utteranceRef.current.onend = null;
      utteranceRef.current.onerror = null;
      utteranceRef.current = null;
    }
    lastElementRef.current = null;

    nukeAudio();
    const interval = setInterval(nukeAudio, 100);
    return () => clearInterval(interval);
  }, [active, paused]);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (utteranceRef.current) {
      utteranceRef.current.onend = null;
      utteranceRef.current.onerror = null;
      utteranceRef.current = null;
    }
    lastElementRef.current = null;
    nukeAudio();
  }, []);

  const speak = useCallback(
    (text: string) => {
      nukeAudio();
      if (utteranceRef.current) {
        utteranceRef.current.onend = null;
        utteranceRef.current.onerror = null;
        utteranceRef.current = null;
      }
      const utterance = new SpeechSynthesisUtterance(text);
      utteranceRef.current = utterance;
      utterance.lang = lang;
      utterance.rate = rate;
      utterance.pitch = pitch;
      const voice = pickVoice(lang, gender);
      if (voice) utterance.voice = voice;
      utterance.onend = () => {
        if (utteranceRef.current === utterance) utteranceRef.current = null;
      };
      utterance.onerror = () => {
        if (utteranceRef.current === utterance) utteranceRef.current = null;
      };
      window.speechSynthesis.speak(utterance);
    },
    [lang, gender, rate, pitch]
  );

  const preview = useCallback(
    (text: string) => {
      speak(text);
    },
    [speak]
  );

  useEffect(() => {
    if (!active || paused) return;

    const handleMouseOver = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (!target || target === lastElementRef.current) return;
      if (suppressRef.current?.contains(target as Node)) return;
      if (!isReadableElement(target)) return;
      lastElementRef.current = target;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const text = extractText(target);
        if (text) speak(text);
      }, HOVER_DELAY_MS);
    };

    const handleMouseOut = (event: MouseEvent) => {
      const related = event.relatedTarget as Node | null;
      if (lastElementRef.current && related && lastElementRef.current.contains(related)) return;
      cancel();
    };

    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);
    return () => {
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
    };
  }, [active, paused, speak, cancel, suppressRef]);

  const toggle = useCallback(() => {
    setActive((prev) => !prev);
  }, []);

  return { active, toggle, preview, cancel };
}
